(() => {
  const money = (value) => `$${value.toFixed(2)}`;
  const clamp = (value, minimum, maximum = Number.POSITIVE_INFINITY) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return minimum;
    return Math.min(maximum, Math.max(minimum, number));
  };

  class InkNativePriceCalculator {
    constructor(root) {
      this.root = root;
      this.thresholds = String(root.dataset.rateThresholds || '').split(',').map(Number);
      this.rates = String(root.dataset.rates || '').split(',').map(Number);
      this.minimumTotal = Number(root.dataset.minimumTotal || 0);
      this.pricingMode = root.dataset.pricingMode || 'profile';
      this.cartUrl = root.dataset.cartUrl || '/cart';
      this.variantMatrix = this.readVariantMatrix();
      this.form = document.getElementById(root.dataset.formId);
      this.customSize = root.querySelector('[data-custom-size]');
      this.customQuantity = root.querySelector('[data-custom-quantity]');
      this.priceTicker = root.querySelector('#priceTicker');
      this.finalValue = root.querySelector('#calculator-final-value');
      this.calculatedPrice = root.querySelector('[data-calculated-price]');
      this.priceDetail = root.querySelector('[data-price-detail]');
      this.summaryUnit = root.querySelector('[data-summary-unit]');
      this.orderSummary = root.querySelector('[data-order-summary]');
      this.tierSummary = root.querySelector('[data-tier-summary]');
      this.checkoutButton = root.querySelector('[data-native-checkout]');
      this.checkoutLabel = root.querySelector('[data-checkout-label]');
      this.checkoutStatus = root.querySelector('[data-checkout-status]');
      this.quantityPicker = root.querySelector('[data-quantity-picker]');
      this.quantityTrigger = root.querySelector('[data-quantity-trigger]');
      this.quantityGuide = root.querySelector('[data-quantity-guide]');
      this.quantityMenu = root.querySelector('[data-quantity-menu]');
      this.quantityCurrent = root.querySelector('[data-quantity-current]');
      this.quantityMinimum = root.querySelector('[data-quantity-minimum]');
      this.checkoutKey = null;
      this.cartRequestInFlight = false;

      const sizeHelp = root.querySelector('[data-size-help]');
      const sizeHelpPanel = root.querySelector('[data-size-help-panel]');
      sizeHelp?.addEventListener('click', () => {
        const open = sizeHelp.getAttribute('aria-expanded') !== 'true';
        sizeHelp.setAttribute('aria-expanded', String(open));
        sizeHelpPanel.hidden = !open;
      });

      root.addEventListener('change', (event) => {
        this.update();
        if (event.target.closest('[data-quantity-option]')) this.setQuantityMenu(false);
      });
      root.addEventListener('input', () => this.update());
      root.querySelectorAll('input[type="number"]').forEach((input) => {
        input.addEventListener('blur', () => {
          const maximum = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
          const decimals = input.step && input.step.includes('.') ? 2 : 0;
          input.value = clamp(input.value, Number(input.min || 0), maximum).toFixed(decimals);
          this.update();
        });
      });

      if (root.dataset.previewMode === 'true' && this.form) {
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          event.stopImmediatePropagation();
          const notice = root.querySelector('[data-preview-message]');
          if (notice) {
            notice.textContent = 'This is a visual prototype. Checkout will be enabled after secure server pricing is connected.';
            notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, true);
      }

      this.checkoutButton?.addEventListener('click', () => this.checkout());
      this.quantityTrigger?.addEventListener('click', () => this.setQuantityMenu(this.quantityMenu?.hidden !== false));
      this.quantityGuide?.addEventListener('click', () => this.setQuantityMenu(this.quantityMenu?.hidden !== false));
      document.addEventListener('click', (event) => {
        if (this.quantityMenu?.hidden !== false) return;
        if (this.quantityPicker?.contains(event.target) || this.quantityGuide?.contains(event.target)) return;
        this.setQuantityMenu(false);
      });
      root.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || this.quantityMenu?.hidden !== false) return;
        this.setQuantityMenu(false);
        this.quantityTrigger?.focus();
      });

      this.update();
    }

    setQuantityMenu(open) {
      if (!this.quantityMenu) return;
      this.quantityMenu.hidden = !open;
      this.quantityTrigger?.setAttribute('aria-expanded', String(open));
      this.quantityGuide?.setAttribute('aria-expanded', String(open));
      this.quantityPicker?.classList.toggle('is-open', open);
    }

    formatDelta(value) {
      const rounded = Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
      if (!rounded) return 'Same price';
      return `${rounded > 0 ? '+' : '−'}${money(Math.abs(rounded))}`;
    }

    readVariantMatrix() {
      const source = this.root.querySelector('[data-variant-matrix]');
      if (!source?.textContent) return [];
      try {
        const variants = JSON.parse(source.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (_error) {
        return [];
      }
    }

    checked(name) {
      return Array.from(this.root.querySelectorAll('[data-calcname]')).find(
        (input) => input.dataset.calcname === name && input.checked
      );
    }

    field(name) {
      return Array.from(this.root.querySelectorAll('[data-calcname]')).find(
        (input) => input.dataset.calcname === name && input.type !== 'radio'
      );
    }

    rateFor(squareInches) {
      let rate = this.rates[0] || 0;
      this.thresholds.forEach((threshold, index) => {
        if (squareInches >= threshold && Number.isFinite(this.rates[index])) rate = this.rates[index];
      });
      return rate;
    }

    totalFor(area, quantity, finishFee) {
      const productionArea = area * quantity;
      const rate = this.rateFor(productionArea);
      const calculated = productionArea * rate / 100 + finishFee;
      const total = Math.round((Math.max(this.minimumTotal, calculated) + Number.EPSILON) * 100) / 100;
      return { total, rate, productionArea };
    }

    selection() {
      const sizeChoice = this.checked('Select Size');
      const quantityChoice = this.checked('Select Quantity');
      const customSize = sizeChoice?.value === 'Custom Size';
      const customQuantity = quantityChoice?.value === 'Custom Quantity';
      const unwindDirection = this.root.querySelector('[name="properties[Unwind direction]"]:checked')?.value || '';
      const rollCoreDiameter = this.root.querySelector('[name="properties[Roll core diameter]"]')?.value || '';
      return {
        shape: this.checked('Select Shape')?.value || '',
        finish: this.checked('Select Finish')?.value || '',
        size: sizeChoice?.value || '',
        width: customSize ? this.field('Width')?.value : undefined,
        height: customSize ? this.field('Length')?.value : undefined,
        quantity: customQuantity
          ? this.field('custom quantity')?.value
          : quantityChoice?.dataset.val,
        ...(unwindDirection ? { unwindDirection } : {}),
        ...(rollCoreDiameter ? { rollCoreDiameter } : {})
      };
    }

    variantFor(selection = this.selection()) {
      return this.variantMatrix.find((variant) => (
        String(variant.option1 || variant.options?.[0] || '') === String(selection.size || '')
        && String(variant.option2 || variant.options?.[1] || '') === String(selection.finish || '')
        && String(variant.option3 || variant.options?.[2] || '') === String(selection.quantity || '')
      )) || null;
    }

    artwork() {
      const upload = this.root.querySelector('[data-ink-artwork-upload]');
      if (!upload || !window.validateInkArtworkUpload?.(this.form)) return null;
      const value = (selector) => upload.querySelector(selector)?.value?.trim() || '';
      const state = upload.querySelector('[data-cloud-upload-status]')?.dataset.state;
      const result = {
        artworkId: value('[data-cloud-upload-id]'),
        versionId: value('[data-cloud-version-id]'),
        objectKey: value('[data-cloud-artwork-key]'),
        shapeObjectKey: value('[data-cloud-shape-key]'),
        notes: upload.querySelector('[name="properties[Artwork notes]"]')?.value?.trim() || ''
      };
      return state === 'ready' && result.artworkId && result.versionId && result.objectKey
        ? result
        : null;
    }

    setCheckoutStatus(message, state = '') {
      if (!this.checkoutStatus) return;
      this.checkoutStatus.textContent = message;
      this.checkoutStatus.dataset.state = state;
      this.checkoutStatus.hidden = !message;
    }

    async checkout() {
      if (this.pricingMode === 'variant') {
        await this.addVariantToCart();
        return;
      }
      if (this.root.dataset.previewMode === 'true') {
        this.setCheckoutStatus('Secure checkout is not enabled for this preview yet.', 'error');
        return;
      }
      const artwork = this.artwork();
      if (!artwork) {
        const upload = this.root.querySelector('[data-ink-artwork-upload]');
        upload?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        upload?.querySelector('[data-upload-error]')?.removeAttribute('hidden');
        this.setCheckoutStatus('Please upload your artwork and wait for the secure upload to finish.', 'error');
        return;
      }

      this.checkoutKey ||= globalThis.crypto?.randomUUID?.()
        || `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('aria-busy', 'true');
      this.checkoutLabel.textContent = 'Preparing secure checkout…';
      this.setCheckoutStatus('Shopify is verifying your price and artwork.', 'loading');

      try {
        const response = await fetch(this.root.dataset.checkoutEndpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: this.root.dataset.productId,
            selection: this.selection(),
            artwork,
            idempotencyKey: this.checkoutKey
          })
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.checkoutUrl) {
          throw new Error(result.error || 'Secure checkout could not be prepared.');
        }
        this.setCheckoutStatus('Price verified. Opening Shopify checkout…', 'success');
        window.location.assign(result.checkoutUrl);
      } catch (error) {
        this.setCheckoutStatus(error.message || 'Secure checkout could not be prepared.', 'error');
        this.checkoutButton.disabled = false;
        this.checkoutButton.removeAttribute('aria-busy');
        this.checkoutLabel.textContent = 'Add to cart';
      }
    }

    syncCustomFields() {
      const customSize = this.pricingMode !== 'variant' && this.checked('Select Size')?.value === 'Custom Size';
      const customQuantity = this.pricingMode !== 'variant' && this.checked('Select Quantity')?.value === 'Custom Quantity';
      for (const [container, enabled] of [[this.customSize, customSize], [this.customQuantity, customQuantity]]) {
        if (!container) continue;
        container.hidden = !enabled;
        container.querySelectorAll('input').forEach((input) => { input.disabled = !enabled; });
      }
    }

    cartProperties() {
      this.syncCustomFields();
      const properties = {};
      if (!this.form) return properties;
      for (const [name, entry] of new FormData(this.form)) {
        if (entry instanceof File) continue;
        const match = name.match(/^properties\[(.+)]$/);
        const value = String(entry).trim();
        if (match && value) properties[match[1]] = value;
      }
      if (properties['Select Shape']) delete properties.Shape;
      return properties;
    }

    async addVariantToCart() {
      if (this.cartRequestInFlight) return;
      const artwork = this.artwork();
      if (!artwork) {
        const upload = this.root.querySelector('[data-ink-artwork-upload]');
        upload?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        upload?.querySelector('[data-upload-error]')?.removeAttribute('hidden');
        this.setCheckoutStatus('Please upload your artwork and wait for the upload to finish.', 'error');
        return;
      }

      const variant = this.variantFor();
      if (!variant?.id || variant.available === false) {
        this.setCheckoutStatus('This option combination is not currently available.', 'error');
        return;
      }

      this.checkoutButton.disabled = true;
      this.cartRequestInFlight = true;
      this.checkoutButton.setAttribute('aria-busy', 'true');
      this.checkoutLabel.textContent = 'Adding to cart…';
      this.setCheckoutStatus('Adding your selected configuration to Shopify cart.', 'loading');

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: Number(variant.id),
              quantity: 1,
              properties: this.cartProperties()
            }]
          })
        });
        const responseText = await response.text();
        let result = {};
        if (responseText) {
          try {
            result = JSON.parse(responseText);
          } catch (error) {
            if (response.ok) throw new Error('Shopify returned an unreadable cart response. Please try again.');
          }
        }
        if (!response.ok) {
          const message = response.status === 429
            ? 'Too many cart requests were sent. Please wait a few seconds, then try again.'
            : (result.description || result.message || 'This item could not be added to cart.');
          throw new Error(message);
        }
        this.setCheckoutStatus('Added. Opening your cart…', 'success');
        window.location.assign(this.cartUrl);
      } catch (error) {
        this.cartRequestInFlight = false;
        this.setCheckoutStatus(error.message || 'This item could not be added to cart.', 'error');
        this.checkoutButton.disabled = false;
        this.checkoutButton.removeAttribute('aria-busy');
        this.checkoutLabel.textContent = 'Add to cart';
      }
    }

    updateVariantQuantityOptions(selection) {
      this.root.querySelectorAll('[data-quantity-option]').forEach((option) => {
        const quantity = String(option.dataset.quantityValue || '');
        const variant = this.variantFor({ ...selection, quantity });
        const total = Number(variant?.price || 0) / 100;
        const numericQuantity = Math.max(1, Number(quantity) || 1);
        option.querySelector('[data-quantity-total]').textContent = variant ? money(total) : 'Unavailable';
        option.querySelector('[data-quantity-unit]').textContent = variant
          ? `${money(total / numericQuantity)} each`
          : '';
        option.querySelector('[data-quantity-message]').hidden = true;
        option.setAttribute('aria-selected', String(quantity === String(selection.quantity)));
        option.classList.toggle('is-unavailable', !variant || variant.available === false);
      });
    }

    updateVariantOptionDeltas(selection, currentTotal) {
      this.root.querySelectorAll('[data-size-option]').forEach((option) => {
        const input = option.querySelector('[data-calcname="Select Size"]');
        const delta = option.querySelector('[data-size-delta]');
        if (!input || !delta) return;
        const variant = this.variantFor({ ...selection, size: input.value });
        delta.textContent = input.checked || !variant
          ? ''
          : this.formatDelta(Number(variant.price || 0) / 100 - currentTotal);
        option.classList.toggle('is-unavailable', !variant || variant.available === false);
      });

      this.root.querySelectorAll('[data-finish-option]').forEach((option) => {
        const input = option.querySelector('[data-calcname="Select Finish"]');
        const delta = option.querySelector('[data-finish-delta]');
        if (!input || !delta) return;
        const variant = this.variantFor({ ...selection, finish: input.value });
        delta.textContent = input.checked || !variant
          ? ''
          : this.formatDelta(Number(variant.price || 0) / 100 - currentTotal);
        option.classList.toggle('is-unavailable', !variant || variant.available === false);
      });
    }

    updateVariantPricing() {
      const selection = this.selection();
      const variant = this.variantFor(selection);
      const quantity = Math.max(1, Number(selection.quantity) || 1);

      if (!variant) {
        this.checkoutButton?.setAttribute('aria-disabled', 'true');
        this.setCheckoutStatus('This option combination is not available.', 'error');
        return;
      }

      const total = Number(variant.price || 0) / 100;
      const each = total / quantity;
      const sizeText = String(selection.size || '').replace(/"/g, '″').replace(/x/i, ' × ');
      const finishText = selection.finish || '';
      const formVariant = this.form?.querySelector('input[name="id"]');
      if (formVariant) formVariant.value = variant.id;

      this.priceTicker.textContent = money(total);
      this.priceTicker.dataset.origmoney = total.toFixed(2);
      this.priceDetail.textContent = `${quantity.toLocaleString()} stickers`;
      this.summaryUnit.textContent = `${money(each)} each`;
      this.orderSummary.textContent = `${sizeText} · ${finishText}`;
      this.tierSummary.textContent = 'Price stored in selected Shopify Variant';
      this.checkoutButton?.setAttribute('aria-disabled', variant.available === false ? 'true' : 'false');
      this.setCheckoutStatus('', '');
      this.updateVariantQuantityOptions(selection);
      this.updateVariantOptionDeltas(selection, total);
      if (this.quantityCurrent) this.quantityCurrent.textContent = quantity.toLocaleString();
      if (this.quantityMinimum) {
        const values = this.variantMatrix.map((item) => Number(item.option3 || item.options?.[2])).filter(Number.isFinite);
        this.quantityMinimum.textContent = Math.min(...values).toLocaleString();
      }

      this.root.dispatchEvent(new CustomEvent('ink:price-change', {
        bubbles: true,
        detail: {
          total,
          quantity,
          finish: finishText,
          size: sizeText,
          shape: selection.shape,
          selection,
          variant
        }
      }));
    }

    updateQuantityOptions(area, finishFee, selectedQuantity) {
      const options = Array.from(this.root.querySelectorAll('[data-quantity-option]'));
      const rows = options.map((option) => {
        const quantity = Number(option.dataset.quantityValue);
        const calculation = this.totalFor(area, quantity, finishFee);
        option.querySelector('[data-quantity-total]').textContent = money(calculation.total);
        option.querySelector('[data-quantity-unit]').textContent = `${money(calculation.total / quantity)} each`;
        option.querySelector('[data-quantity-message]').hidden = true;
        option.setAttribute('aria-selected', String(quantity === selectedQuantity));
        return { option, quantity, ...calculation };
      });

      const selectedIndex = rows.findIndex((row) => row.quantity === selectedQuantity);
      const selected = rows[selectedIndex];
      const next = rows[selectedIndex + 1];
      if (selected && next) {
        const message = selected.option.querySelector('[data-quantity-message]');
        message.innerHTML = `↓ Add <b>${(next.quantity - selected.quantity).toLocaleString()}</b> more for only <b>${money(next.total - selected.total)}</b>`;
        message.hidden = false;
      }
    }

    updateOptionDeltas(currentTotal, quantity, area, finishFee) {
      this.root.querySelectorAll('[data-size-option]').forEach((option) => {
        const input = option.querySelector('[data-calcname="Select Size"]');
        const delta = option.querySelector('[data-size-delta]');
        if (!input || !delta) return;
        if (input.value === 'Custom Size' && !input.checked) {
          delta.textContent = 'Enter size';
          return;
        }
        const candidateArea = input.value === 'Custom Size'
          ? clamp(this.field('Width')?.value, 1.5, 10) * clamp(this.field('Length')?.value, 1.5, 10)
          : Number(input.dataset.val || area);
        const candidate = this.totalFor(candidateArea, quantity, finishFee);
        delta.textContent = input.checked ? '' : this.formatDelta(candidate.total - currentTotal);
      });

      this.root.querySelectorAll('[data-finish-option]').forEach((option) => {
        const input = option.querySelector('[data-calcname="Select Finish"]');
        const delta = option.querySelector('[data-finish-delta]');
        if (!input || !delta) return;
        const candidate = this.totalFor(area, quantity, Number(input.dataset.fee || 0));
        delta.textContent = input.checked ? '' : this.formatDelta(candidate.total - currentTotal);
      });
    }

    update() {
      this.syncCustomFields();
      if (this.pricingMode === 'variant') {
        this.updateVariantPricing();
        return;
      }
      const sizeChoice = this.checked('Select Size');
      const quantityChoice = this.checked('Select Quantity');
      const finishChoice = this.checked('Select Finish');
      const customSizeSelected = sizeChoice?.value === 'Custom Size';
      const customQuantitySelected = quantityChoice?.value === 'Custom Quantity';

      const width = customSizeSelected ? clamp(this.field('Width')?.value, 1.5, 10) : 0;
      const height = customSizeSelected ? clamp(this.field('Length')?.value, 1.5, 10) : 0;
      const area = customSizeSelected ? width * height : Number(sizeChoice?.dataset.val || 4);
      const quantity = customQuantitySelected
        ? Math.round(clamp(this.field('custom quantity')?.value, 50))
        : Number(quantityChoice?.dataset.val || 50);
      const finishFee = Number(finishChoice?.dataset.fee || 0);
      const { productionArea, rate, total } = this.totalFor(area, quantity, finishFee);
      const each = total / quantity;
      const sizeText = customSizeSelected
        ? `${width.toFixed(2).replace(/\.00$/, '')}″ × ${height.toFixed(2).replace(/\.00$/, '')}″`
        : (sizeChoice?.value || '2x2').replace('x', '″ × ') + '″';
      const finishText = finishChoice?.value || 'Gloss';

      this.priceTicker.textContent = money(total);
      this.priceTicker.dataset.origmoney = total.toFixed(2);
      this.finalValue.value = total.toFixed(2);
      this.calculatedPrice.value = total.toFixed(2);
      this.priceDetail.textContent = `${quantity.toLocaleString()} stickers`;
      this.summaryUnit.textContent = `${money(each)} each`;
      this.orderSummary.textContent = `${sizeText} · ${finishText}`;
      this.tierSummary.textContent = quantity >= 100 ? 'Volume discount included' : 'Price updates instantly';
      this.updateQuantityOptions(area, finishFee, customQuantitySelected ? null : quantity);
      this.updateOptionDeltas(total, quantity, area, finishFee);
      if (this.quantityCurrent) this.quantityCurrent.textContent = quantity.toLocaleString();
      if (this.quantityMinimum) {
        const presetQuantities = Array.from(this.root.querySelectorAll('[data-quantity-option]'))
          .map((option) => Number(option.dataset.quantityValue))
          .filter(Number.isFinite);
        this.quantityMinimum.textContent = Math.min(...presetQuantities, Number(this.root.dataset.minQuantity || 50)).toLocaleString();
      }

      const selection = this.selection();
      this.root.dispatchEvent(new CustomEvent('ink:price-change', {
        bubbles: true,
        detail: {
          total,
          quantity,
          area,
          rate,
          finish: finishText,
          size: sizeText,
          shape: selection.shape,
          selection
        }
      }));
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-ink-native-calculator]').forEach((root) => {
      if (root.dataset.nativeCalculatorReady === 'true') return;
      root.dataset.nativeCalculatorReady = 'true';
      new InkNativePriceCalculator(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
